import React from 'react';
import {Table} from '@tanstack/react-table';
import {TranslateFn} from './ts-data-table.contracts';

const DEFAULT_SIBLING_COUNT = 1;

interface TsPaginationProps<TData> {
    table: Table<TData>;
    totalRows: number;
    translate: TranslateFn;
    siblingCount?: number;
}

function getPageNumbers(currentPage: number, totalPages: number, siblingCount: number): (number | '...')[] {
    const totalPageNumbers = siblingCount * 2 + 5;

    if (totalPages <= totalPageNumbers) {
        return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [1];
    const leftBound = currentPage - siblingCount;
    const rightBound = currentPage + siblingCount;

    if (leftBound > 2) {
        pages.push('...');
    }

    const start = Math.max(2, leftBound);
    const end = Math.min(totalPages - 1, rightBound);

    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    if (rightBound < totalPages - 1) {
        pages.push('...');
    }

    pages.push(totalPages);

    return pages;
}

export function TsPagination<TData>({
    table,
    totalRows,
    translate,
    siblingCount = DEFAULT_SIBLING_COUNT
}: TsPaginationProps<TData>) {
    const {pageIndex, pageSize} = table.getState().pagination;
    const pageCount = table.getPageCount();
    const currentPage = pageIndex + 1;

    if (totalRows === 0 || pageCount <= 1) {
        return null;
    }

    const pages = getPageNumbers(currentPage, pageCount, siblingCount);

    return (
        <div className='ts-pagination'>
            <button
                className='ts-pagination-arrow'
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                title={translate('REACT.DATA_TABLE.PREVIOUS_PAGE')}>
                {'‹'}
            </button>

            {pages.map((page, idx) =>
                page === '...' ? (
                    <span key={`ellipsis-${idx}`} className='ts-pagination-ellipsis'>
                        …
                    </span>
                ) : (
                    <button
                        key={page}
                        className={`ts-pagination-page ${page === currentPage ? 'active' : ''}`}
                        onClick={() => table.setPageIndex(page - 1)}>
                        {page}
                    </button>
                )
            )}

            <button
                className='ts-pagination-arrow'
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                title={translate('REACT.DATA_TABLE.NEXT_PAGE')}>
                {'›'}
            </button>
        </div>
    );
}
